import React, { useState, useMemo } from 'react';
import { Drawer, Box, List, Toolbar, Typography, Divider, IconButton, Avatar, Tooltip, TextField, InputAdornment, useTheme, useMediaQuery } from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Logout as LogoutIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { getMenuForApp, MenuItem } from '../../config/menuConfig';
import SidebarItem from './SidebarItem';
import '../SidebarStyles.css'; // Import custom styles
import Logo from '../Logo';
import { useAuth } from '../../contexts/AuthContext';
import { useApplication } from '../../contexts/ApplicationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMenu } from '../../contexts/MenuContext';

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 70;

interface SidebarProps {
    isCollapsed: boolean;
    onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
    const { user } = useAuth();
    const { currentApp, currentAppVersion } = useApplication();



    // ... (in Sidebar component)

    const { t, dir } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchTerm, setSearchTerm] = useState('');
    const { menuItems } = useMenu(); // Use dynamic menu items



    const currentWidth = isCollapsed ? (isMobile ? 0 : COLLAPSED_WIDTH) : DRAWER_WIDTH;
    const isRtl = dir === 'rtl';

    // Filter logic

    // Filter logic
    const filteredItems = useMemo(() => {
        if (!searchTerm) return menuItems;

        const lowerTerm = searchTerm.toLowerCase();

        const filterRecursive = (items: MenuItem[]): MenuItem[] => {
            return items.reduce((acc: MenuItem[], item) => {
                const caption = item.translationKey ? t(item.translationKey) : item.caption;
                const matches = caption.toLowerCase().includes(lowerTerm);
                const hasChildren = item.child && item.child.length > 0;

                // If parent matches, include it and ALL its children (no filtering on children)
                if (matches) {
                    acc.push(item);
                    return acc;
                }

                // If parent doesn't match, check children
                if (hasChildren) {
                    const filteredChildren = filterRecursive(item.child || []);
                    if (filteredChildren.length > 0) {
                        // Create a new object to avoid mutating original, with filtered children
                        acc.push({ ...item, child: filteredChildren });
                    }
                }

                return acc;
            }, []);
        };

        return filterRecursive(menuItems);
    }, [searchTerm, menuItems]);

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            anchor="left"
            open={isMobile ? !isCollapsed : true}
            onClose={isMobile ? onToggle : undefined}
            ModalProps={{
                keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
                width: currentWidth,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: isMobile ? DRAWER_WIDTH : currentWidth,
                    boxSizing: 'border-box',
                    borderRight: isRtl ? 'none' : 'none', // Remove border as we use glassmorphism
                    borderLeft: isRtl ? 'none' : 'none',
                    transition: (theme) => theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    overflowX: 'hidden',
                },
            }}
            PaperProps={{
                className: `glass-sidebar sidebar-scroll ${isCollapsed && !isMobile ? 'collapsed' : ''}`,
                dir: dir,
                sx: {
                    backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#1e293b',
                    color: 'text.primary',
                }
            }}
        >
            <Toolbar sx={{
                display: 'flex',
                alignItems: 'center',
                px: isCollapsed ? 1 : 2,
                gap: 2,
                justifyContent: isCollapsed ? 'center' : 'space-between'
            }}>
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Logo variant="white" height={32} />
                        <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {currentApp ? currentApp : 'BLOBTIC'}
                        </Typography>
                    </Box>
                )}
                <IconButton onClick={onToggle} sx={{ color: 'white' }}>
                    {isRtl ?
                        (isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />) :
                        (isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)
                    }
                </IconButton>
            </Toolbar>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Search Bar */}
            {!isCollapsed && (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder={t('sidebar.search.placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.4)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255, 255, 255, 0.5)',
                                opacity: 1,
                            },
                        }}
                        autoComplete="off"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}

            <Box sx={{ overflowY: 'auto', overflowX: 'hidden', mt: 1, flexGrow: 1 }}>
                <List>
                    {filteredItems.map((item, index) => {
                        if (item.for_admin && !user?.isAdmin) return null;
                        return <SidebarItem key={index} item={item} collapsed={isCollapsed} forceOpen={!!searchTerm} />;
                    })}
                </List>
            </Box>

            {/* Sidebar Footer Application Version */}
            {!isCollapsed && (
                <Box
                    sx={{
                        p: 1.25,
                        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                        textAlign: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.45)',
                            fontSize: '0.72rem',
                            letterSpacing: '0.03em',
                            fontWeight: 500,
                        }}
                    >
                        Blobtic {currentApp || 'ERP'} {currentAppVersion ? `• ${currentAppVersion}` : ''}
                    </Typography>
                </Box>
            )}
        </Drawer>
    );
};

export default Sidebar;
